import { observer } from 'mobx-react-lite'
import {
  Alert,
  Button,
  Divider,
  Icon,
  SpinnerLoader,
  TotalDueTodayRow,
  Typography,
} from 'shared/ui'
import { numberFormat } from 'shared/lib'
import { PageNumberInfo } from 'shared/ui/PageNumberInfo/PageNumberInfo'
import { DropdownCards } from 'entities/Billing/ui/DropdownCards'
import { SecurePaymentInfo } from 'pages/settings/ui/BillingPlan/ui/SecurePaymentInfo/SecurePaymentInfo'
import { LineItem } from 'pages/settings/ui/BillingPlan/ui/LineItem/LineItem'
import { useUpgradeLayoutBackChrome } from 'pages/settings/pages/billing/pages/upgrade/hooks/useUpgradeLayoutBackChrome'
import { DowngradeEffectiveDateAlert } from 'pages/settings/pages/billing/pages/upgrade/ui/DowngradeEffectiveDateAlert/DowngradeEffectiveDateAlert'
import { UpgradePeriodInfo } from 'pages/settings/pages/billing/pages/upgrade/ui/UpgradePeriodInfo/UpgradePeriodInfo'
import { useBillingUpgradePageContext } from 'pages/settings/pages/billing/pages/upgrade/context/upgradeContext'
import styles from './styles.module.scss'

export const UpgradeCheckoutPage = observer(() => {
  const store = useBillingUpgradePageContext()
  const { previousStep, currentStep, totalSteps, isDowngrade } = store

  useUpgradeLayoutBackChrome(previousStep)

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerNav}>
          <PageNumberInfo
            currentStep={currentStep}
            totalSteps={totalSteps}
            title={isDowngrade ? 'Downgrade' : 'Upgrade'}
          />
        </div>
        <Typography
          ariaLabel='billing_upgrade_checkout_page_title'
          className={styles.title}
          variant='body-xl-medium'
          tag='div'
          marginTop={36}
        >
          Checkout
        </Typography>
      </div>

      <div className={styles.content}>
        {store.paymentError && (
          <Alert
            item={{
              type: 'error',
              title: 'We were unable to process your payment',
              desc: 'Please select a different card',
            }}
            marginBottom={16}
          />
        )}

        <UpgradePeriodInfo
          className={styles.timePeriodInfo}
          period={store.period}
          handleSetPeriod={store.handleSetPeriod}
          isPlanLoading={store.isPlanLoading}
          annualSavings={store.annualSavings}
          shouldShowPeriodTabs={store.shouldShowPeriodTabs}
          selectedPlanInfo={null}
          isUpgradeModalV2_2={store.isUpgradeModalV2_2}
          showDivider={false}
        />

        {isDowngrade && (
          <DowngradeEffectiveDateAlert
            effectiveDate={store.downgradeEffectiveDate ?? undefined}
            marginBottom={24}
          />
        )}

        {store.selectedPlanInfo && (
          <div
            className={styles.planInfoBox}
            style={store.isUpgradeModalV2_2 ? { marginTop: 0 } : undefined}
          >
            <div className={styles.planInfoHeader}>
              <Icon
                icon={store.selectedPlanInfo.icon}
                fontSize={24}
                color={store.selectedPlanInfo.iconColor}
              />
              <Typography ariaLabel='plan_name' variant='body-lg-medium'>
                {store.selectedPlanInfo.name}
              </Typography>
            </div>
            <Typography ariaLabel='plan_credits' variant='body-md-regular'>
              {numberFormat({ value: store.selectedPlanInfo.credits })} credits /{' '}
              {store.period === 'annually' ? 'year' : 'month'}
            </Typography>
            <Typography ariaLabel='additional_credit_price' variant='body-md-regular'>
              {numberFormat({
                value: store.selectedPlanInfo.additionalCreditPrice,
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 4,
              })}
              &nbsp;per additional credit
            </Typography>
          </div>
        )}

        <div className={styles.totalPlanData}>
          {store.isPlanLoading && (
            <div className={styles.planLoading}>
              <SpinnerLoader ariaLabel='UpgradeCheckoutPage' size={32} />
            </div>
          )}
          <div className={styles.totalPlanDataLines}>
            {store.planLines.map((item) => (
              <LineItem key={item.title} title={item.title} value={item.value} />
            ))}
          </div>
          <Divider style={{ borderColor: 'var(--black-20)', margin: '12px 0' }} />
          <div className={styles.totalPlanDataLines}>
            {store.totalPlanData.map((item) => (
              <LineItem
                key={item.title}
                title={item.title}
                value={numberFormat({
                  value: item.value,
                  currency: 'USD',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                color={item.color}
              />
            ))}
          </div>
          <Divider style={{ borderColor: 'var(--black-20)', margin: '12px 0' }} />

          <TotalDueTodayRow
            totalValue={store.totalDueToday}
            nextChargeAmountDollars={numberFormat({
              value: (store.planData?.next_charge_details?.total ?? 0) / 100,
              currency: 'USD',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            nextChargeDate={store.planData?.next_charge_details?.date}
          />
        </div>

        {!isDowngrade && (
          <div className={styles.dropdownCardsWrapper}>
            <DropdownCards store={store.dropdownCardsStore} />
          </div>
        )}

        <div className={styles.buttonsWrapper}>
          {isDowngrade ? (
            <>
              <Button
                fullWidth
                text='Keep my current plan'
                typeBtn='contained'
                contained='primary'
                size='extraLarge'
                intent='default'
                className={styles.keepCurrentPlanButton}
                onClick={store.handleKeepCurrentPlan}
              />
              <Button
                fullWidth
                text='Downgrade plan'
                contained='secondary'
                size='extraLarge'
                intent='destructive'
                className={styles.downgradePlanButton}
                onClick={() => store.upgradePlan(true)}
                loading={store.isUpgrading}
                disabled={store.isUpgrading || store.downgradeContentStore.selectedReason === null}
              />
            </>
          ) : (
            <Button
              fullWidth
              text='Upgrade plan'
              typeBtn='contained'
              contained='primary'
              size='extraLarge'
              intent='positive'
              className={styles.upgradePlanButton}
              onClick={() => store.upgradePlan()}
              loading={store.isUpgrading}
              disabled={store.isUpgrading}
            />
          )}
        </div>
        <SecurePaymentInfo className={styles.securePaymentInfo} />
      </div>
    </div>
  )
})
